import React, {
  useState,
  useEffect,
  createContext,
  ReactNode,
  useContext
} from 'react';
import { get } from '../../redesign/javascript/utils/fetch';
import camelCaseKeys from 'camelcase-keys';

export type OrganizationProject = {
  id: number;
  name: string;
  updatedAt: string;
  showOnLive: boolean;
};

type Organization = {
  id: number;
  experiments: string[];
  projects: OrganizationProject[];
  acceptingFeedbackAllowed: boolean;
  feedbackDeadlineAllowed: boolean;
};

type OrganizationState = {
  loading: boolean;
  error: any;
  organization: Organization | null;
  hasOrganizationExperiment: (experiment: string) => boolean;
  hasNewOnboardingExperiment: boolean;
  hasProxyEnabledExperiment: boolean;
};

const organizationCache = new Map<string, Organization>();

const organizationCacheKey = (id: number, apiDomain: string) =>
  `${apiDomain}:${id}`;

// @ts-ignore
const OrganizationStateContext = createContext<OrganizationState>();

const OrganizationProvider = ({
  apiDomain = '',
  id,
  children
}: {
  apiDomain?: string;
  id?: number;
  children: ReactNode;
}) => {
  const cached =
    id != null ? organizationCache.get(organizationCacheKey(id, apiDomain)) : undefined;
  const [organization, setOrganization] = useState<Organization | null>(
    cached ?? null
  );
  const [loading, setLoading] = useState<boolean>(!cached && !!id);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    if (!id) return;

    let isMounted = true;
    const key = organizationCacheKey(id, apiDomain);
    const fromCache = organizationCache.get(key);
    if (fromCache) {
      setOrganization(fromCache);
      setLoading(false);
    } else {
      setLoading(true);
    }

    const fetchOrganization = async () => {
      setError(null);
      try {
        const response = await get(`${apiDomain}/organizations/${id}`);
        const data = camelCaseKeys(response, { deep: true });
        organizationCache.set(key, data);
        if (isMounted) setOrganization(data);
      } catch (err) {
        if (isMounted) {
          console.error('Failed to fetch organization:', err);
          setError(err);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchOrganization();

    return () => {
      isMounted = false;
    };
  }, [id, apiDomain]);

  const hasOrganizationExperiment = (experiment: string) => {
    return !!organization?.experiments.includes(experiment);
  };

  const hasNewOnboardingExperiment =
    hasOrganizationExperiment('checklist_onboarding_variant') ||
    hasOrganizationExperiment('add_website_onboarding_variant') ||
    hasOrganizationExperiment('add_website_onboarding_no_proxy');

  const hasProxyEnabledExperiment =
    hasOrganizationExperiment('checklist_onboarding_variant') ||
    hasOrganizationExperiment('add_website_onboarding_variant');

  return (
    <OrganizationStateContext.Provider
      value={{
        organization,
        loading,
        error,
        hasOrganizationExperiment,
        hasNewOnboardingExperiment,
        hasProxyEnabledExperiment
      }}
    >
      {children}
    </OrganizationStateContext.Provider>
  );
};

const useOrganizationState = (): OrganizationState => {
  const context = useContext(OrganizationStateContext);

  if (context === undefined) {
    throw new Error(
      'useOrganizationState must be used within a OrganizationProvider'
    );
  }

  // @ts-ignore
  return context;
};

// Export the context and provider
export { OrganizationStateContext, OrganizationProvider, useOrganizationState };
